/**
 * Yearly and multi-year analysis engine for PV installations.
 *
 * - 12-month analysis: compares monthly production vs neighbors and prognosis
 * - 5-year analysis: detects long-term patterns without neighbor comparison
 *
 * @module yearlyAnalysis
 */

import { MONTHLY_SOLAR_FRACTIONS } from './constants'
import { MONTH_NAMES_NL as MONTH_NAMES } from './formatters'

function meanStd(values) {
  if (values.length === 0) return { mean: 0, std: 0 }
  const mean = values.reduce((a, b) => a + b, 0) / values.length
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length
  return { mean, std: Math.sqrt(variance) }
}

// ─── 12-month analysis (with neighbors) ─────────────────────────────────────

/**
 * Analyze 12 months of monthly production data vs neighbors and prognosis.
 *
 * @param {Object} params
 * @param {Array}  params.mainMonthly       - [{date, production}] monthly aggregates
 * @param {Object} params.neighborMonthlyMap - { serialNumber: [{date, production}] }
 * @param {Array}  params.neighbors          - [{ serialNumber, address }]
 * @param {number|null} params.mainWp
 * @param {Object} params.neighborWpMap
 * @param {string} params.matchType
 * @param {number|null} params.expectedProduction - Annual expected production (Wh)
 * @returns {Object|null}
 */
export function analyzeYear({
  mainMonthly,
  neighborMonthlyMap,
  neighbors,
  mainWp,
  neighborWpMap,
  matchType,
  expectedProduction,
}) {
  if (!mainMonthly || mainMonthly.length === 0) return null

  const mainKwp = mainWp ? mainWp * 0.001 : null
  const label = matchType === 'postcode' ? 'vergelijkbare installaties' : 'buren'
  const hasNeighbors = neighbors?.length > 0

  // Build per-month data (normalized to Wh/kWp)
  const mainByMonth = {} // { 0..11: production Wh/kWp }
  let mainYearTotal = 0

  mainMonthly.forEach(item => {
    const date = new Date(item.date)
    const month = date.getMonth()
    const val = Math.max(0, item.production ?? 0)
    const norm = mainKwp ? val / mainKwp : val
    mainByMonth[month] = (mainByMonth[month] || 0) + norm
    mainYearTotal += val
  })

  const monthsWithData = Object.keys(mainByMonth).filter(m => mainByMonth[m] > 0).length
  if (monthsWithData < 3) return null

  // Neighbor monthly data
  const neighborByMonth = {} // { serialNumber: { 0..11: production Wh/kWp } }
  const activeNeighbors = []

  if (hasNeighbors) {
    for (const n of neighbors) {
      const nData = neighborMonthlyMap?.[n.serialNumber]
      if (!nData || nData.length === 0) continue
      const nWp = neighborWpMap?.[n.serialNumber]
      const nKwp = (nWp ? nWp * 0.001 : null) || mainKwp
      const nByMonth = {}
      nData.forEach(item => {
        const month = new Date(item.date).getMonth()
        const val = Math.max(0, item.production ?? 0)
        nByMonth[month] = (nByMonth[month] || 0) + (nKwp ? val / nKwp : val)
      })
      if (Object.keys(nByMonth).length >= 3) {
        neighborByMonth[n.serialNumber] = nByMonth
        activeNeighbors.push(n)
      }
    }
  }

  const segments = []

  // 1. Prognosis comparison per month
  let prognosisData = null
  if (expectedProduction) {
    prognosisData = {}
    const monthlyRatios = []
    let cumulativeActual = 0
    let cumulativeExpected = 0

    for (let m = 0; m < 12; m++) {
      const expected = expectedProduction * MONTHLY_SOLAR_FRACTIONS[m]
      const actual = mainByMonth[m] ? mainByMonth[m] * (mainKwp || 1) : null // back to Wh
      prognosisData[m] = { expected, actual, ratio: actual != null && expected > 0 ? actual / expected : null }
      if (actual != null) {
        cumulativeActual += actual
        cumulativeExpected += expected
        if (expected > 0) monthlyRatios.push(actual / expected)
      }
    }

    const yearRatio = cumulativeExpected > 0 ? Math.round((cumulativeActual / cumulativeExpected) * 100) : null
    prognosisData.yearRatio = yearRatio
    prognosisData.cumulativeActual = cumulativeActual
    prognosisData.cumulativeExpected = cumulativeExpected

    // Find months significantly under prognosis
    const underMonths = []
    for (let m = 0; m < 12; m++) {
      const r = prognosisData[m]?.ratio
      if (r !== null && r < 0.6 && prognosisData[m].expected > expectedProduction * 0.04) {
        underMonths.push({ month: m, ratio: Math.round(r * 100) })
      }
    }

    if (yearRatio !== null) {
      if (yearRatio < 70) {
        segments.push({ text: `Jaarproductie is ${yearRatio}% van prognose — significant onder verwachting.`, type: 'critical' })
      } else if (yearRatio < 85) {
        segments.push({ text: `Jaarproductie is ${yearRatio}% van prognose — onder verwachting.`, type: 'warning' })
      } else if (yearRatio <= 110) {
        segments.push({ text: `Jaarproductie is ${yearRatio}% van prognose — op schema.`, type: 'good' })
      } else {
        segments.push({ text: `Jaarproductie is ${yearRatio}% van prognose — boven verwachting.`, type: 'good' })
      }
    }

    if (underMonths.length > 0) {
      const monthNames = underMonths.map(u => `${MONTH_NAMES[u.month]} (${u.ratio}%)`)
      segments.push({ text: `Maanden onder prognose: ${monthNames.join(', ')}.`, type: 'warning' })
    }
  }

  // 2. Seasonal shadow detection (compare winter vs summer ratio to neighbors)
  let seasonalShadow = null
  if (activeNeighbors.length > 0) {
    const winterMonths = [10, 11, 0, 1] // nov–feb (low sun)
    const summerMonths = [4, 5, 6, 7]   // may–aug (high sun)

    const ratioForSeason = (months) => {
      let mainSum = 0, nSum = 0, count = 0
      months.forEach(m => {
        if (!mainByMonth[m]) return
        mainSum += mainByMonth[m]
        activeNeighbors.forEach(n => {
          const nVal = neighborByMonth[n.serialNumber]?.[m]
          if (nVal) { nSum += nVal; count++ }
        })
      })
      const nAvg = count > 0 ? nSum / activeNeighbors.length : 0
      return nAvg > 0 ? mainSum / nAvg : null
    }

    const winterRatio = ratioForSeason(winterMonths)
    const summerRatio = ratioForSeason(summerMonths)

    if (winterRatio !== null && summerRatio !== null) {
      seasonalShadow = {
        winterRatio: Math.round(winterRatio * 100),
        summerRatio: Math.round(summerRatio * 100),
      }

      if (winterRatio < 0.6 && summerRatio > 0.85) {
        segments.push({
          text: `Seizoensgebonden schaduw: winterproductie ${Math.round(winterRatio * 100)}% van ${label}, zomer ${Math.round(summerRatio * 100)}%. Obstakel blokkeert laagstaande zon.`,
          type: 'warning',
        })
      } else if (summerRatio < 0.6 && winterRatio > 0.85) {
        segments.push({
          text: `Onverwacht: zomerproductie lager (${Math.round(summerRatio * 100)}%) dan winter (${Math.round(winterRatio * 100)}%) t.o.v. ${label}. Mogelijk omvormer-clipping bij hoge instraling.`,
          type: 'warning',
        })
      }
    }

    // 3. Per-month ratio to neighbors
    const monthlyRatios = []
    for (let m = 0; m < 12; m++) {
      if (!mainByMonth[m]) continue
      let nSum = 0, nCount = 0
      activeNeighbors.forEach(n => {
        const nVal = neighborByMonth[n.serialNumber]?.[m]
        if (nVal) { nSum += nVal; nCount++ }
      })
      if (nCount > 0) {
        monthlyRatios.push({ month: m, ratio: mainByMonth[m] / (nSum / nCount) })
      }
    }

    // Overall year ratio
    const avgYearRatio = monthlyRatios.length > 0
      ? Math.round((monthlyRatios.reduce((s, r) => s + r.ratio, 0) / monthlyRatios.length) * 100)
      : null

    // Find worst performing months vs neighbors
    const worstMonths = monthlyRatios.filter(r => r.ratio < 0.6).sort((a, b) => a.ratio - b.ratio)
    if (worstMonths.length > 0 && worstMonths.length <= 4) {
      const names = worstMonths.map(w => `${MONTH_NAMES[w.month]} (${Math.round(w.ratio * 100)}%)`)
      segments.push({ text: `Slechtste maanden vs ${label}: ${names.join(', ')}.`, type: 'warning' })
    }

    return {
      segments,
      prognosisData,
      seasonalShadow,
      monthlyRatios,
      summary: {
        yearRatioToNeighbors: avgYearRatio,
        yearRatioToPrognosis: prognosisData?.yearRatio ?? null,
        monthsWithData,
        neighborsUsed: activeNeighbors.length,
        seasonalShadow,
      },
    }
  }

  return {
    segments,
    prognosisData,
    seasonalShadow: null,
    monthlyRatios: [],
    summary: {
      yearRatioToNeighbors: null,
      yearRatioToPrognosis: prognosisData?.yearRatio ?? null,
      monthsWithData,
      neighborsUsed: 0,
      seasonalShadow: null,
    },
  }
}

// ─── 5-year analysis (no neighbors) ─────────────────────────────────────────

/**
 * Analyze up to 5 years of monthly production data.
 * Detects long-term degradation, seasonal anomalies, communication patterns.
 *
 * @param {Object} params
 * @param {Array}  params.monthlyData        - [{date, production}] all monthly aggregates
 * @param {number|null} params.mainWp
 * @param {number|null} params.expectedProduction
 * @param {string|null} params.dateActivated  - Activation date ISO string
 * @returns {Object|null}
 */
export function analyzeFiveYear({
  monthlyData,
  mainWp,
  expectedProduction,
  dateActivated,
}) {
  if (!monthlyData || monthlyData.length === 0) return null

  const mainKwp = mainWp ? mainWp * 0.001 : null

  // Organize by year-month
  const byYearMonth = {} // { 'YYYY-MM': production }
  const byYear = {}      // { YYYY: { months: {0..11: val}, total: N } }

  monthlyData.forEach(item => {
    const date = new Date(item.date)
    const y = date.getFullYear()
    const m = date.getMonth()
    const val = Math.max(0, item.production ?? 0)
    const norm = mainKwp ? val / mainKwp : val
    const key = `${y}-${String(m + 1).padStart(2, '0')}`

    byYearMonth[key] = norm
    if (!byYear[y]) byYear[y] = { months: {}, total: 0, rawTotal: 0 }
    byYear[y].months[m] = norm
    byYear[y].total += norm
    byYear[y].rawTotal += val
  })

  const years = Object.keys(byYear).map(Number).sort()
  const dataSpanYears = years.length

  if (dataSpanYears < 1) return null

  const segments = []

  // 1. Year-over-year degradation detection
  if (dataSpanYears >= 2) {
    const yearTotals = years.map(y => ({ year: y, total: byYear[y].total, rawTotal: byYear[y].rawTotal }))

    // Only compare full years (or years with similar month coverage)
    const comparableYears = yearTotals.filter(yt => {
      const monthCount = Object.keys(byYear[yt.year].months).length
      return monthCount >= 10 // at least 10 months of data
    })

    if (comparableYears.length >= 2) {
      const first = comparableYears[0]
      const last = comparableYears[comparableYears.length - 1]
      const yearSpan = last.year - first.year

      if (yearSpan >= 1 && first.total > 0) {
        const totalChange = ((last.total - first.total) / first.total) * 100
        const annualChange = totalChange / yearSpan

        if (annualChange < -5) {
          segments.push({
            text: `Jaarlijkse degradatie van ${Math.abs(annualChange).toFixed(1)}% per jaar over ${yearSpan} jaar (${first.year}: ${Math.round(first.total)} → ${last.year}: ${Math.round(last.total)} Wh/kWp). Dit is boven de normale paneel-degradatie (~0.5%/jaar).`,
            type: 'critical',
          })
        } else if (annualChange < -2) {
          segments.push({
            text: `Lichte afname van ${Math.abs(annualChange).toFixed(1)}% per jaar over ${yearSpan} jaar. Kan wijzen op vervuiling of slijtage.`,
            type: 'warning',
          })
        } else if (annualChange < -0.3) {
          segments.push({
            text: `Normale degradatie (${Math.abs(annualChange).toFixed(1)}%/jaar over ${yearSpan} jaar).`,
            type: 'good',
          })
        } else {
          segments.push({
            text: `Stabiele of stijgende productie over ${yearSpan} jaar.`,
            type: 'good',
          })
        }
      }
    }
  }

  // 2. Per-year prognosis comparison
  let yearlyPrognosis = null
  if (expectedProduction && dataSpanYears >= 1) {
    yearlyPrognosis = {}
    years.forEach(y => {
      const monthCount = Object.keys(byYear[y].months).length
      if (monthCount < 6) return // skip incomplete years

      const actualRaw = byYear[y].rawTotal
      // Scale prognosis to months with data
      let scaledExpected = 0
      Object.keys(byYear[y].months).forEach(m => {
        scaledExpected += expectedProduction * MONTHLY_SOLAR_FRACTIONS[Number(m)]
      })

      const ratio = scaledExpected > 0 ? Math.round((actualRaw / scaledExpected) * 100) : null
      yearlyPrognosis[y] = { actual: actualRaw, expected: scaledExpected, ratio, monthCount }
    })

    // Report years significantly under prognosis
    const underYears = Object.entries(yearlyPrognosis)
      .filter(([, v]) => v.ratio !== null && v.ratio < 75)
      .map(([y, v]) => `${y} (${v.ratio}%)`)

    if (underYears.length > 0) {
      segments.push({ text: `Jaren onder prognose: ${underYears.join(', ')}.`, type: 'warning' })
    }
  }

  // 3. Seasonal consistency: compare same month across years
  const seasonalAnomalies = []
  if (dataSpanYears >= 2) {
    for (let m = 0; m < 12; m++) {
      const monthValues = years
        .map(y => byYear[y]?.months[m])
        .filter(v => v != null && v > 0)

      if (monthValues.length >= 2) {
        const { mean, std } = meanStd(monthValues)
        // Find years where this month was an outlier
        years.forEach(y => {
          const val = byYear[y]?.months[m]
          if (val != null && val > 0 && std > 0) {
            const z = (val - mean) / std
            if (z < -1.5 && val < mean * 0.5) {
              seasonalAnomalies.push({ year: y, month: m, value: Math.round(val), avg: Math.round(mean), pct: Math.round((val / mean) * 100) })
            }
          }
        })
      }
    }

    if (seasonalAnomalies.length > 0 && seasonalAnomalies.length <= 5) {
      const details = seasonalAnomalies.map(a => `${MONTH_NAMES[a.month]} ${a.year} (${a.pct}% van gemiddeld)`)
      segments.push({ text: `Opvallend lage maanden: ${details.join(', ')}.`, type: 'warning' })
    } else if (seasonalAnomalies.length > 5) {
      segments.push({ text: `${seasonalAnomalies.length} maanden met opvallend lage productie t.o.v. historisch gemiddelde.`, type: 'warning' })
    }
  }

  // 4. Communication pattern over years
  const commGaps = []
  if (dataSpanYears >= 1) {
    // Find months with zero or missing data
    const activationDate = dateActivated ? new Date(dateActivated) : null
    const now = new Date()

    years.forEach(y => {
      for (let m = 0; m < 12; m++) {
        const monthDate = new Date(y, m, 15)
        if (activationDate && monthDate < activationDate) continue
        if (monthDate > now) continue

        const val = byYear[y]?.months[m]
        if (val == null || val === 0) {
          // Check if it's a productive month (should have sun)
          if (MONTHLY_SOLAR_FRACTIONS[m] > 0.04) {
            commGaps.push({ year: y, month: m })
          }
        }
      }
    })

    if (commGaps.length > 0) {
      // Group consecutive gaps
      if (commGaps.length <= 3) {
        const names = commGaps.map(g => `${MONTH_NAMES[g.month]} ${g.year}`)
        segments.push({ text: `Ontbrekende data: ${names.join(', ')}.`, type: 'warning' })
      } else {
        segments.push({ text: `${commGaps.length} maanden met ontbrekende of nul-productie data.`, type: 'critical' })
      }
    }
  }

  // 5. Snow/fouling pattern: sudden zero months in winter
  if (dataSpanYears >= 2) {
    const winterZeros = commGaps.filter(g => [11, 0, 1, 2].includes(g.month))
    const summerZeros = commGaps.filter(g => [4, 5, 6, 7, 8].includes(g.month))

    if (summerZeros.length > 0) {
      segments.push({
        text: `Nul-productie in zomermaanden (${summerZeros.map(g => `${MONTH_NAMES[g.month]} ${g.year}`).join(', ')}) — onverwacht, mogelijk meterstoring.`,
        type: 'critical',
      })
    }
  }

  // 6. Installation age and expected lifetime
  if (dateActivated) {
    const ageYears = Math.round((new Date() - new Date(dateActivated)) / (365.25 * 86400000) * 10) / 10
    if (ageYears > 20) {
      segments.push({ text: `Installatie is ${ageYears} jaar oud — nadert einde verwachte levensduur (25 jaar).`, type: 'warning' })
    } else if (ageYears > 15) {
      segments.push({ text: `Installatie is ${ageYears} jaar oud. Verhoogde aandacht voor degradatie.`, type: 'normal' })
    }
  }

  // Summary
  const summary = {
    dataSpanYears,
    yearsAvailable: years,
    totalDataMonths: Object.keys(byYearMonth).length,
    commGapCount: commGaps.length,
    seasonalAnomalyCount: seasonalAnomalies.length,
    yearlyPrognosis,
  }

  return {
    segments,
    byYear,
    byYearMonth,
    yearlyPrognosis,
    summary,
  }
}
